import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../store/store";
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { getQlikApp } from '../../services/qlikService';
import {
  clearFieldByName,
  applySelectionByFieldName,
} from "../../services/qlikSelectionService";
import { getFieldValues } from "../../services/qlikFieldService";
import { useReady } from "../../hooks/useReady";
import { gtm } from '../../services/gtm';
import { QLIK_APP_REF } from "../../constants";
import './FilterDropDown.scss';

interface FilterDropdownProps {
    formName: string;
    fieldName: string;
    fieldLabel: string;
    multiSelect?: boolean;
    showOnlyOpen?: boolean;
    qlikSort?: string;
    sortExpression?: string;
    altState?: string;
    required?: boolean;
    removeValues?: string[];
}

interface QlikListItem {
    qText: string;
    qNum?: number;
    qState: 'S' | 'O' | 'X' | 'A';
    qElemNumber: number;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
    formName,
    fieldName,
    fieldLabel,
    multiSelect = false,
    showOnlyOpen = false,
    qlikSort = 'qSortByLoadOrder',
    sortExpression,
    altState = '',
    required = false,
    removeValues = [],
}) => {
    const [loading, setLoading] = useState(true);
    const { qlikFields } = useReady(QLIK_APP_REF, "", "", fieldName);
    //   const [selectedValues, setSelectedValues] = useState<QlikListItem[]>([]);
    const [selectedValue, setSelectedValue] = useState<any>(
    multiSelect ? [] : ""
    );
    const selectQlikFieldsValList = (state: RootState) => state.qlikFields.qlikFieldsValList;
    const makeSelectFieldDefs = () =>
    createSelector(
        [selectQlikFieldsValList, (_: RootState, app: string) => app, (_: RootState, __: string, name: string) => name],
        (qlikFieldsValList, app, name) => qlikFieldsValList[app]?.[name] || []
    );
    const selectFieldDefs = useMemo(makeSelectFieldDefs, []);
    const fieldDefs = useSelector((state: RootState) => selectFieldDefs(state, QLIK_APP_REF, fieldName));

    const items = useMemo(() => {
        const valList = fieldDefs || [];
        let filteredList = valList;

        // Filter by state if showOnlyOpen is true
        if (showOnlyOpen) {
            filteredList = filteredList.filter((item: any) => item[0].qState !== "X");
        }

        // Remove values specified in removeValues prop
        if (removeValues.length > 0) {
            filteredList = filteredList.filter(
                (item: any) => !removeValues.includes(item[0].qText)
            );
        }

        return filteredList;
    }, [fieldDefs, showOnlyOpen, removeValues]);

    useEffect(() => {
        fetchValues();
    }, [fieldName, altState]);

    useEffect(() => {
        setAppliedFilter();
    }, [items]);

    const fetchValues = async () => {
        try {
            if(!qlikFields) {
            setLoading(true);
            await getFieldValues(fieldName, QLIK_APP_REF, 250, qlikSort, sortExpression || '', altState);
        }
        } catch (error) {
            console.error('Error fetching filter values:', error);
        } finally {
            setLoading(false);
        }
    };


    const applySelectedValues = async (selectedValue: any) => {
        setLoading(true);
        let selectedValues: any[] = [];

        if (multiSelect) {
            selectedValues = Array.isArray(selectedValue)
                ? selectedValue.map((filter: any) =>
                    filter[0].qNum === "NaN" ? filter[0].qText : filter[0].qNum
                )
                : [selectedValue];
        }
        else {
            selectedValues = Array.isArray(selectedValue)
            ? selectedValue[0].qNum === "NaN" ? [selectedValue[0].qText] : [selectedValue[0].qNum]
            : [selectedValue];
            
        }
        await applySelectionByFieldName(QLIK_APP_REF, fieldName, selectedValues, altState);
        setLoading(false);
        gtm.selection(fieldName, selectedValues.join(', '), formName);
    };

    const clearField = async () => {
        setLoading(true);
        await clearFieldByName(QLIK_APP_REF, fieldName, altState);
        setLoading(false);
        gtm.clearSelection(fieldName);
    };

    const setAppliedFilter = () => {
        const selectedItems = items.filter(
        (item: any) => item[0].qState === "S"
        );
        const optionalItems = items.filter(
        (item: any) => item[0].qState === "O"
        );

        // If only one optional value is available, show it as selected
        const newValue = selectedItems.length
        ? selectedItems
        // : optionalItems.length === 1
        // ? optionalItems
        : [];

        setSelectedValue(multiSelect ? newValue : newValue?.[0] || "");
    };

    return (
        <Box sx={{ position: 'relative', mb: 2, minWidth: '170px', maxWidth: '400px' }}>
            <Autocomplete
                multiple={multiSelect}
                options={items}
                size='small'
                value={selectedValue || null}
                onChange={(event, value, reason) => {
                    setSelectedValue(value);
                    reason !== 'clear' ? applySelectedValues(value) : clearField();
                }}
                // open={true}   //Use this for debugging to keep the dropdown always open
                getOptionLabel={(option: any) => option?.[0]?.qText || ""}
                loading={loading}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                    <li
                        key={key}
                        {...rest}
                        className={`${props.className} ${option[0].qState === "X" ? "autocomplete-option-optional" : ""}`}
                    >
                        {option[0].qText}
                    </li>
                    )
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={fieldLabel}
                        disabled={loading}
                        required={required}
                    />
                )}
            />
        </Box>
    );
};

export default FilterDropdown;